import type { AiMapTarget } from './liveTargets';

/**
 * The AI assistant's short-lived marks on the map (WI #5767, D4): a created
 * or changed entity glows briefly, a removed one fades out where it was.
 * Kept outside the reactive store — the canvas reads them every frame while
 * they last; the store bumps `effectsVersion` so the map starts repainting.
 */

/** The AI's own presence colour — none of the colleagues' colours. */
export const AI_PRESENCE_COLOR = '#C026D3';

/** How long a saved entity glows. */
export const GLOW_MS = 1600;
/** How long "Auf Karte zeigen" highlights an answer's entities. */
export const HIGHLIGHT_GLOW_MS = 3000;
/** How long a removed entity takes to fade out. */
export const FADE_MS = 1200;

export interface AiGlow {
  kind: 'glow';
  /** The run's project / template (`liveKey`). */
  key: string;
  target: AiMapTarget;
  startedAt: number;
  duration: number;
}

/** A removed entity, drawn where it was until it has faded out. */
export interface AiGhost {
  kind: 'ghost';
  key: string;
  type: 'activity' | 'milestone';
  /** Timeline position, so the ghost moves with pan and zoom. */
  pos: number;
  /** Screen Y. */
  y: number;
  startedAt: number;
  duration: number;
}

export type AiEffect = AiGlow | AiGhost;

let effects: AiEffect[] = [];

function sameTarget(effect: AiEffect, key: string, target: AiMapTarget) {
  return (
    effect.kind === 'glow' &&
    effect.key === key &&
    effect.target.type === target.type &&
    effect.target.id === target.id
  );
}

/** Lets an entity glow; one that glows already starts over. */
export function addAiGlow(
  key: string,
  target: AiMapTarget,
  duration = GLOW_MS,
  now = performance.now(),
): void {
  effects = effects.filter((effect) => !sameTarget(effect, key, target));
  effects.push({ kind: 'glow', key, target, startedAt: now, duration });
}

export function addAiGhost(
  key: string,
  ghost: Pick<AiGhost, 'type' | 'pos' | 'y'>,
  now = performance.now(),
): void {
  effects.push({
    kind: 'ghost',
    key,
    ...ghost,
    startedAt: now,
    duration: FADE_MS,
  });
}

/** 0 at the start of an effect, 1 at its end. */
export function effectProgress(effect: AiEffect, now: number): number {
  return Math.min(1, Math.max(0, (now - effect.startedAt) / effect.duration));
}

function alive(effect: AiEffect, now: number): boolean {
  return now - effect.startedAt < effect.duration;
}

/** The effects of one project / template map that still run. */
export function aiEffectsFor(key: string, now = performance.now()): AiEffect[] {
  effects = effects.filter((effect) => alive(effect, now));

  return effects.filter((effect) => effect.key === key);
}

/** Whether the map has to keep repainting. */
export function hasAiEffects(now = performance.now()): boolean {
  return effects.some((effect) => alive(effect, now));
}

export function clearAiEffects(): void {
  effects = [];
}
